import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Autocomplete,
  TextField,
  Avatar,
  CircularProgress,
  Divider,
  Badge,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { JamIcon } from 'components/ui';
import ListBox from 'components/ListBox';
import shortid from 'shortid';

const MedicalTaskEscalationInput = ({
  value = [],
  onChange,
  practitioners = [],
  practitionersLoading = false,
  disabled = false,
  placeholder = 'Select practitioner',
  onPractitionerSearch,
  fetchMorePractitioners,
  ...props
}) => {
  const { t } = useTranslation();
  
  // Infinite scroll for practitioners
  const infiniteScrollPractitioners = (e) => {
    const scrollTop = e.target.scrollTop;
    const scrollHeight = e.target.scrollHeight;
    const clientHeight = e.target.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 1) {
      fetchMorePractitioners && fetchMorePractitioners();
    }
  };
  const [tempEscalation, setTempEscalation] = useState({
    careTeamMemberId: null,
    escalationDuration: { 
      minutes: '', 
      hours: '', 
      days: '', 
      months: '', 
      years: '' 
    }
  });

  const resetForm = () => {
    setTempEscalation({
      careTeamMemberId: null,
      escalationDuration: { 
        minutes: '', 
        hours: '', 
        days: '', 
        months: '', 
        years: '' 
      }
    });
  };

  const handleAddEscalation = () => {
    if (!tempEscalation.careTeamMemberId) return;
    
    // Validate that at least one time unit is specified
    const { minutes, hours, days, months, years } = tempEscalation.escalationDuration;
    const numMinutes = parseFloat(minutes) || 0;
    const numHours = parseFloat(hours) || 0;
    const numDays = parseFloat(days) || 0;
    const numMonths = parseFloat(months) || 0;
    const numYears = parseFloat(years) || 0;
    
    // Check if at least one time unit has a value
    if (numMinutes === 0 && numHours === 0 && numDays === 0 && 
        numMonths === 0 && numYears === 0) return;

    const newEscalation = {
      id: shortid.generate(),
      careTeamMemberId: tempEscalation.careTeamMemberId.id,
      escalationDuration: {
        ...(numMinutes > 0 && { minutes: numMinutes }),
        ...(numHours > 0 && { hours: numHours }),
        ...(numDays > 0 && { days: numDays }),
        ...(numMonths > 0 && { months: numMonths }),
        ...(numYears > 0 && { years: numYears })
      }
    };

    const updatedRules = [...value, newEscalation];
    onChange(updatedRules);
    
    // Auto-clear form after successful addition
    resetForm();
  };

  const handleRemoveEscalation = (index) => {
    const updatedRules = value.filter((_, i) => i !== index);
    onChange(updatedRules);
  };

  const getPractitionerName = (practitionerId) => {
    const practitioner = practitioners.find(p => p.id === practitionerId);
    if (!practitioner) return t('Unknown');
    
    return `${practitioner.firstName} ${practitioner.lastName}`;
  };

  const getPractitionerAvatar = (practitionerId) => {
    const practitioner = practitioners.find(p => p.id === practitionerId);
    return practitioner?.photoUrl || '';
  };

  const formatDurationDisplay = (escalationRule) => {
    const { minutes, hours, days, months, years } = escalationRule.escalationDuration;
    const parts = [];
    
    if (years > 0) parts.push(`${years} ${t('years')}`);
    if (months > 0) parts.push(`${months} ${t('months')}`);
    if (days > 0) parts.push(`${days} ${t('days')}`);
    if (hours > 0) parts.push(`${hours} ${t('hours')}`);
    if (minutes > 0) parts.push(`${minutes} ${t('minutes')}`);
    
    return parts.length > 0 ? parts.join(' ') : t('Not specified');
  };

  const isAddDisabled = () => {
    if (!tempEscalation.careTeamMemberId) return true;
    
    const { minutes, hours, days, months, years } = tempEscalation.escalationDuration;
    const numMinutes = parseFloat(minutes) || 0;
    const numHours = parseFloat(hours) || 0;
    const numDays = parseFloat(days) || 0;
    const numMonths = parseFloat(months) || 0;
    const numYears = parseFloat(years) || 0;
    
    return numMinutes === 0 && numHours === 0 && numDays === 0 && 
           numMonths === 0 && numYears === 0;
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {t('Escalation Rules')}
      </Typography>

      {/* Existing Escalation Rules List - Now at TOP */}
      {value && value.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('Added Escalations')}
            </Typography>
            <Badge 
              badgeContent={value.length} 
              color="primary" 
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.75rem',
                  minWidth: 18,
                  height: 18,
                }
              }}
            >
              <Box sx={{ width: 16, height: 16 }} />
            </Badge>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {value.map((rule, index) => (
              <Box
                key={rule.id || index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  border: 1,
                  borderColor: 'success.light',
                  borderRadius: 1,
                  backgroundColor: 'success.50',
                  position: 'relative',
                  '&:before': {
                    content: `"${index + 1}"`,
                    position: 'absolute',
                    left: -8,
                    top: -8,
                    width: 20,
                    height: 20,
                    backgroundColor: 'success.main',
                    color: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }
                }}
              >
                <Box display="flex" alignItems="center" gap={2} pl={1}>
                  <Avatar 
                    src={getPractitionerAvatar(rule.careTeamMemberId)}
                    sx={{ width: 32, height: 32 }}
                  />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getPractitionerName(rule.careTeamMemberId)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {t('Escalates after')} {formatDurationDisplay(rule)}
                    </Typography>
                  </Box>
                </Box>
                
                {!disabled && (
                  <IconButton
                    onClick={() => handleRemoveEscalation(index)}
                    color="error"
                    size="small"
                    sx={{ '&:hover': { backgroundColor: 'error.50' } }}
                  >
                    <JamIcon icon="close" size={16} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
          
          {/* Visual divider */}
          <Divider sx={{ mt: 3, mb: 2 }} />
        </Box>
      )}

      {/* Add New Escalation Form - Now at BOTTOM */}
      <Box sx={{ 
        p: 2.5, 
        border: 2, 
        borderColor: 'primary.light', 
        borderRadius: 2, 
        backgroundColor: 'background.paper',
        borderStyle: 'dashed',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'primary.50',
        }
      }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          + {t('Add New Escalation')}
        </Typography>
        
        {/* Practitioner Selection */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            {t('Escalate to')}
          </Typography>
          <Autocomplete
            options={practitioners}
            value={tempEscalation.careTeamMemberId}
            loading={practitionersLoading}
            disabled={disabled}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            ListboxProps={{ onScroll: infiniteScrollPractitioners }}
            ListboxComponent={ListBox}
            onChange={(e, newValue) => {
              setTempEscalation(prev => ({
                ...prev,
                careTeamMemberId: newValue
              }));
            }}
            onInputChange={onPractitionerSearch}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: tempEscalation.careTeamMemberId && (
                    <Avatar 
                      src={getPractitionerAvatar(tempEscalation.careTeamMemberId.id)}
                      sx={{ width: 20, height: 20, mr: 1 }}
                    />
                  ),
                  endAdornment: (
                    <>
                      {practitionersLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id} display="flex" alignItems="center" gap={1}>
                <Avatar 
                  src={option.photoUrl}
                  sx={{ width: 28, height: 28 }} 
                />
                <Typography variant="body2">{`${option.firstName} ${option.lastName}`}</Typography>
              </Box>
            )}
          />
        </Box>

        {/* Duration Input - All time units with decimal support */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            {t('After')}
          </Typography>
          
          {/* Time units */}
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <TextField
              type="number"
              label={t('Years')}
              value={tempEscalation.escalationDuration.years}
              onChange={(e) => setTempEscalation(prev => ({
                ...prev,
                escalationDuration: { ...prev.escalationDuration, years: e.target.value }
              }))}
              disabled={disabled}
              size="small"
              inputProps={{ min: 0, step: 0.1 }}
              sx={{ width: 80 }}
            />
            <TextField
              type="number"
              label={t('Months')}
              value={tempEscalation.escalationDuration.months}
              onChange={(e) => setTempEscalation(prev => ({
                ...prev,
                escalationDuration: { ...prev.escalationDuration, months: e.target.value }
              }))}
              disabled={disabled}
              size="small"
              inputProps={{ min: 0, step: 0.1 }}
              sx={{ width: 80 }}
            />
            <TextField
              type="number"
              label={t('Days')}
              value={tempEscalation.escalationDuration.days}
              onChange={(e) => setTempEscalation(prev => ({
                ...prev,
                escalationDuration: { ...prev.escalationDuration, days: e.target.value }
              }))}
              disabled={disabled}
              size="small"
              inputProps={{ min: 0, step: 0.1 }}
              sx={{ width: 80 }}
            />
            <TextField
              type="number"
              label={t('Hours')}
              value={tempEscalation.escalationDuration.hours}
              onChange={(e) => setTempEscalation(prev => ({
                ...prev,
                escalationDuration: { ...prev.escalationDuration, hours: e.target.value }
              }))}
              disabled={disabled}
              size="small"
              inputProps={{ min: 0, step: 0.1 }}
              sx={{ width: 80 }}
            />
            <TextField
              type="number"
              label={t('Minutes')}
              value={tempEscalation.escalationDuration.minutes}
              onChange={(e) => setTempEscalation(prev => ({
                ...prev,
                escalationDuration: { ...prev.escalationDuration, minutes: e.target.value }
              }))}
              disabled={disabled}
              size="small"
              inputProps={{ min: 0, step: 0.1 }}
              sx={{ width: 80 }}
            />
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={handleAddEscalation}
          disabled={disabled || isAddDisabled()}
          size="small"
        >
          + {t('Add Escalation')}
        </Button>
      </Box>
    </Box>
  );
};

export default MedicalTaskEscalationInput;